import { createApi } from "@reduxjs/toolkit/query/react";
import {
  listCatalogResponse,
  listCatalogRequest,
  catalogCountResponse,
  countCatalogRequest,
} from "../../types";
import { axiosBaseQuery } from "../../providers/auth0-instance";
import { withErrorNotification } from "../notifications";
import { generateUniqueCatalogId } from "../../utils/helpers";
import { upsertCatalogItems } from "../../reducers/catalogItemsSlice";

export const catalogApi = createApi({
  reducerPath: "catalogApi",
  baseQuery: axiosBaseQuery(),
  tagTypes: ["Catalog"],
  refetchOnFocus: true,
  refetchOnReconnect: true,
  endpoints: (builder) => ({
    listCatalog: builder.query<listCatalogResponse, listCatalogRequest>({
      query: (params) => ({
        url: "/catalog",
        method: "GET",
        params,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.items.map(
                ({ source, id }) =>
                  ({
                    type: "Catalog",
                    id: generateUniqueCatalogId(source, id),
                  }) as const,
              ),
              { type: "Catalog", id: "LIST" },
            ]
          : [{ type: "Catalog", id: "LIST" }],
      onQueryStarted: async (_args, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          dispatch(upsertCatalogItems(data.items));
        } catch (error) {
          withErrorNotification("Failed to fetch catalog images");
        }
      },
    }),
    countCatalog: builder.query<catalogCountResponse, countCatalogRequest>({
      query: (params) => ({
        url: "/catalog/count",
        method: "GET",
        params,
      }),
      providesTags: [{ type: "Catalog", id: "COUNT" }],
      onQueryStarted: withErrorNotification("Failed to fetch catalog count"),
    }),
    invalidateCatalog: builder.mutation<null, void>({
      // RTK Query treats `data: undefined` as neither a data nor an error result and logs
      // a console.error; null satisfies the shape check while still meaning "no payload"
      queryFn: () => ({ data: null }),
      invalidatesTags: ["Catalog"],
    }),
  }),
});

export const {
  useListCatalogQuery,
  useCountCatalogQuery,
  usePrefetch,
  useInvalidateCatalogMutation,
} = catalogApi;
